import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useIsStripeConnected } from '@elfsight/lib-stripe-integration/widget';
import { Show } from '@elfsight/lib-utils';
import { CheckoutComponent } from '@elfsight/widget-checkout';
import { Button, Typography } from '@elfsight/widget-ui';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { EmailSubmitPopup } from '../components/email-submit-popup/email-submit-popup';
import { useEmailSubmitPopup } from '../use-email-submit-popup';
import { useCheckoutErrorText, useCheckoutSettings } from './checkout';

const ERROR_COLOR = '#FF3838';

export function CTA({ isMobile }: { isMobile?: boolean }) {
  const {
    resultCTAHeading,
    resultCTACaption,
    resultButtonText,
    resultButtonLink,
    actionButtonColor,
    editMode,
    resultButtonAction
  } = useSettings();

  const [displayCheckoutError, setDisplayCheckoutError] = useState('');

  const checkoutErrorText = useCheckoutErrorText();
  const isStripeConnected = useIsStripeConnected();
  const checkoutSettings = useCheckoutSettings();
  const areCheckoutSettingsValid = !!checkoutSettings;

  const { isFormPopupOpen, openFormPopup, closeFormPopup } =
    useEmailSubmitPopup();

  const handleClick = useCallback(() => {
    if (isStripeConnected) {
      if (!areCheckoutSettingsValid) {
        setDisplayCheckoutError(checkoutErrorText);

        return;
      }

      setDisplayCheckoutError('');
    }

    if (resultButtonAction === 'openForm') {
      openFormPopup();

      return;
    }

    const { rawValue = '', target } = resultButtonLink ?? {};
    const linkURL = rawValue.trim();

    if (linkURL) {
      window.open(linkURL, editMode ? '_blank' : target);
    }
  }, [
    editMode,
    resultButtonLink,
    resultButtonAction,
    openFormPopup,
    isStripeConnected,
    areCheckoutSettingsValid,
    checkoutErrorText
  ]);

  const showCTA =
    resultButtonAction !== 'noAction' &&
    (resultCTAHeading || resultCTACaption || resultButtonText);

  if (!showCTA) {
    return null;
  }

  return (
    <Container>
      <Show when={!!resultCTAHeading}>
        <Typography
          lineHeight="28px"
          textStyle={{ fontSize: isMobile ? 22 : 24, fontWeight: 'bold' }}
        >
          {resultCTAHeading}
        </Typography>
      </Show>

      <Show when={!!resultCTACaption}>
        <Typography
          lineHeight="20px"
          textStyle={{ fontSize: 15 }}
          dangerouslySetInnerHTML={{ __html: resultCTACaption }}
        />
      </Show>

      <Show when={!!resultButtonText}>
        <Button
          aria-label={resultButtonText}
          color={actionButtonColor}
          fontColor={getInvertedColor(actionButtonColor)}
          onClick={handleClick}
          font={{ fontSize: 15, fontWeight: 'bold' }}
          style={{
            marginTop: resultCTAHeading || resultCTACaption ? 8 : undefined
          }}
          fullWidth
        >
          {resultButtonText}
        </Button>
      </Show>

      <Show when={isStripeConnected}>
        <Show when={!!displayCheckoutError}>
          <Typography textToken="text13" color={ERROR_COLOR}>
            {displayCheckoutError}
          </Typography>
        </Show>

        <Show when={areCheckoutSettingsValid}>
          <CheckoutComponent {...checkoutSettings!} />
        </Show>
      </Show>

      <Show when={!isStripeConnected}>
        <EmailSubmitPopup
          show={isFormPopupOpen}
          onCloseRequest={closeFormPopup}
        />
      </Show>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 24px;
`;
